import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SearchableOption } from './searchable-select';

interface SearchableMultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: SearchableOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Label like "{n} selected" — receives the count. */
  selectedLabel?: (count: number) => string;
  className?: string;
  disabled?: boolean;
}

/**
 * Multi-select dropdown with a type-to-filter search box and checkboxes.
 * Sibling of SearchableSelect; stays open while toggling. Includes a
 * select-all / clear toggle scoped to the current filter.
 *
 * The panel is rendered through a portal with fixed positioning so it can
 * escape ancestors that clip overflow (e.g. the `overflow-hidden` Card).
 */
export function SearchableMultiSelect({
  values,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  selectedLabel = (n) => `${n} selected`,
  className = '',
  disabled = false,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const selected = new Set(values);

  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReflow = () => updatePos();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updatePos]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange([...next]);
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => selected.has(o.value));

  const toggleAllFiltered = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filtered.forEach((o) => next.delete(o.value));
    else filtered.forEach((o) => next.add(o.value));
    onChange([...next]);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 text-left text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
      >
        <span className={`truncate ${values.length ? '' : 'text-gray-400'}`}>
          {values.length ? selectedLabel(values.length) : placeholder}
        </span>
        <span className="text-gray-400">▾</span>
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 9999 }}
            className="rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
          >
            <div className="flex items-center gap-2 p-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 flex-1 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-100"
              />
              {filtered.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllFiltered}
                  className="whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {allFilteredSelected ? '✕' : '✓'}
                </button>
              )}
            </div>
            <ul className="max-h-56 overflow-y-auto pb-1">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-400">{emptyText}</li>
              )}
              {filtered.map((o) => (
                <li key={o.value}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={selected.has(o.value)}
                      onChange={() => toggle(o.value)}
                      className="rounded border-gray-300"
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
